/**
 * Egyptian bone domino tile rendering.
 *
 * Tiles are drawn as aged ivory bone slabs with recessed black-lacquer pips,
 * an engraved divider and a brass spinner pin. Every internal measurement is
 * percentage-based, so a tile renders crisply at ANY pixel size (hand rack,
 * board world, guide cards, mini backs).
 */

import { getTile } from "@domino/rules-engine";
import React from "react";

/** Pip positions per value: [col, row] in a 3x3 grid. */
const PIP_LAYOUT: Record<number, Array<[number, number]>> = {
  0: [],
  1: [[2, 2]],
  2: [
    [1, 1],
    [3, 3],
  ],
  3: [
    [1, 1],
    [2, 2],
    [3, 3],
  ],
  4: [
    [1, 1],
    [3, 1],
    [1, 3],
    [3, 3],
  ],
  5: [
    [1, 1],
    [3, 1],
    [2, 2],
    [1, 3],
    [3, 3],
  ],
  6: [
    [1, 1],
    [3, 1],
    [1, 2],
    [3, 2],
    [1, 3],
    [3, 3],
  ],
};

export function Half({ value }: { value: number }) {
  const pips = PIP_LAYOUT[value] ?? [];
  return (
    <div className={`domino-half ${pips.length === 0 ? "domino-half-blank" : ""}`}>
      {pips.map(([col, row], i) => (
        <span key={i} className="domino-pip" style={{ gridColumn: col, gridRow: row }} />
      ))}
    </div>
  );
}

export interface TileFaceProps {
  tileId: number;
  /** Explicit halves in display order, e.g. [inward, outward] for chain tiles. */
  halves?: [number, number];
  /** Render lying down (long axis horizontal). */
  horizontal?: boolean;
  selected?: boolean;
  dimmed?: boolean;
  raised?: boolean;
  isGhost?: boolean;
  isDragging?: boolean;
  /** Fresh placement highlight (brass ring that fades). */
  justPlayed?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onPointerDown?: (e: React.PointerEvent) => void;
  title?: string;
  size?: "sm" | "md" | "lg" | "xl";
  style?: React.CSSProperties;
  className?: string;
}

export function TileFace({
  tileId,
  halves,
  horizontal = false,
  selected = false,
  dimmed = false,
  raised = false,
  isGhost = false,
  isDragging = false,
  justPlayed = false,
  onClick,
  onPointerDown,
  title,
  size = "md",
  style,
  className,
}: TileFaceProps) {
  const tile = getTile(tileId);
  const firstVal = halves ? halves[0] : tile.high;
  const secondVal = halves ? halves[1] : tile.low;

  const cls = [
    "domino-tile",
    `domino-size-${size}`,
    horizontal ? "domino-horizontal" : "domino-vertical",
    selected ? "domino-selected" : "",
    dimmed ? "domino-dimmed" : "",
    raised ? "domino-raised" : "",
    isGhost ? "domino-ghost" : "",
    isDragging ? "domino-dragging" : "",
    justPlayed ? "domino-just-played" : "",
    onClick || onPointerDown ? "domino-interactive" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls} style={style} onClick={onClick} onPointerDown={onPointerDown} title={title}>
      <div className="domino-bone">
        <Half value={firstVal} />
        <div className="domino-divider">
          <span className="domino-spinner-pin" />
        </div>
        <Half value={secondVal} />
      </div>
      <div className="domino-sheen" />
      {justPlayed && <div className="domino-played-ring" />}
    </div>
  );
}

/** Face-down tile back: dark walnut with a brass inlay star. */
export function TileBack({
  horizontal = false,
  size = "md",
  style,
  className,
}: {
  horizontal?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <div
      className={`domino-tile domino-back domino-size-${size} ${
        horizontal ? "domino-horizontal" : "domino-vertical"
      } ${className ?? ""}`}
      style={style}
    >
      <div className="domino-back-inner">
        <span className="domino-back-star" aria-hidden />
        <span className="domino-back-stud" aria-hidden />
      </div>
    </div>
  );
}
